import React, { useState } from 'react';
import IdentificationPartialForm from './PartialForm/IdentificationPartialForm';
import {
    checkIfKnownIbanBceCombination,
    formatNumberAddress,
    formatNumberCompany,
    formatPhone,
    handleDeleteFile,
    handleFileValidation,
    isValidNationalRegisterNumber,
} from './FormHelper';
import AddressPartialForm from './PartialForm/AddressPartialForm';
import ContactPartialForm from './PartialForm/ContactPartialForm';
import SectorPartialForm from './PartialForm/SectorPartialForm';
import { IbanOwnerType } from './Types/IbanOwnerType';
import { useTranslation } from '../../Helpers';
import { WidgetHelper } from '../WidgetHelper';
import { FloatLabel } from '../../Components';
import { validateIncitantForm } from './FormValidator';
import { PrimeSuccessWidget } from './PrimeSuccessWidget';
import { IIncitantFormData } from './Types/IIncitantFormData';
import { PrimeIncitantService } from '../../Services/PrimeIncitantService';
import FileUploadField from './PartialForm/FileUploadField';

interface Props {
    language: string;
    translationJson: string;
}

const PrimeIncitantForm = ({ language, translationJson }: Props) => {
    const [data, setData] = useState<IIncitantFormData>({
        name: '',
        surname: '',
        denominationCompany: '',
        numberCompany: '',
        socialCompany: '',
        addressStreet: '',
        addressNumber: '',
        addressPostal: '',
        addressLocality: '',
        mail: '',
        mailConfirmation: '',
        phone: '',
        phoneConfirmation: '',
        iban: '',
        isIbanOk: false,
        bic: '',
        isKnownCombinationCompanyIBAN: false,
        ibanOwner: IbanOwnerType.OwnerAsker,
        sectorCompany: '',
        workCompany: '',
        commissionCompany: '',
        trainingInvoiceFile: null as any,
        trainingName: '',
        trainingPaymentFile: null as any,
        trainingRegistrationDate: '',
        bankAttestationFile: null as any,
        employmentContractFile: null as any,
        workerName: '',
        workerNiss: '',
        workerNissIsValid: false,
        workerSurname: '',
        dataAreCorrect: false,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof IIncitantFormData, string>>
    >({});
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const translations = useTranslation(translationJson) as Record<
        string,
        string
    >;

    const borderRadius = {
        borderRadius: '5px',
    } as React.CSSProperties;

    const handleChange = <K extends keyof IIncitantFormData>(
        field: K,
        value: IIncitantFormData[K]
    ) => {
        setData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleChangeNumberCompany = (value: string) => {
        const formatted = formatNumberCompany(value);
        setData((prev: any) => ({ ...prev, numberCompany: formatted }));
    };

    const handleBlurNumberCompany = async () => {
        if (data.iban && data.numberCompany && data.isIbanOk) {
            const isKnown = await checkIfKnownIbanBceCombination(
                data.iban,
                data.numberCompany
            );
            setData((prev: any) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: isKnown,
            }));
        } else {
            setData((prev: any) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: false,
            }));
        }
    };

    const handleBlurNumberCompanyVoid = () => {
        void handleBlurNumberCompany();
    };

    const handleChangeNumberAddress = (value: string) => {
        const formatted = formatNumberAddress(value);
        setData((prev: any) => ({ ...prev, addressNumber: formatted }));
    };

    const handleChangePhone = (value: string, field: string) => {
        const phoneFormatted = formatPhone(value);
        setData((prev: any) => ({ ...prev, [field]: phoneFormatted }));
    };

    const handleSelectSector = (event: { sector: string; work: string }) => {
        setData((prev: any) => ({
            ...prev,
            sectorCompany: event.sector,
            workCompany: event.work,
        }));
    };

    const handleChangeNationalRegisterNumber = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = event.target;

        const match = input.value
            .replace(/\D/g, '')
            .match(/(\d{1,2})(\d{1,2})?(\d{1,2})?(\d{1,3})?(\d{1,2})?/);

        if (match) {
            const [, a, b, c, d, e] = match;

            input.value =
                `${a}` +
                (b ? `.${b}` : '') +
                (c ? `.${c}` : '') +
                (d ? `-${d}` : '') +
                (e ? `.${e}` : '');
        }

        const cleanValue = match ? match.slice(1).join('') : '';
        const isValid = isValidNationalRegisterNumber(cleanValue);

        setData((prev) => ({
            ...prev,
            workerNiss: input.value,
            workerNissIsValid: isValid,
        }));
    };

    const handleChangeCommission = (value: string) => {
        const digits = value.replace(/\D/g, '');
        const prefix = translations['Site.Prime.CommissionValue'] + ' ';

        const formatted =
            digits.length === 0
                ? ''
                : digits.length < 4
                  ? prefix + digits.padEnd(3, '0').substring(0, 3)
                  : prefix +
                    `${digits.substring(0, 3)}.${digits.substring(3, 5)}`;

        setData((prev) => ({ ...prev, commissionCompany: formatted }));
    };

    const handleChangeFiles = <K extends keyof IIncitantFormData>(
        e: React.ChangeEvent<HTMLInputElement>,
        name: K
    ) => {
        if (e.target.files && e.target.files[0]) {
            handleFileValidation<IIncitantFormData>(e.target.files[0], {
                fieldName: name,
                setData,
            });
        }
    };

    const onFileDelete = <K extends keyof IIncitantFormData>(
        name: K,
        e?: React.MouseEvent
    ) => {
        e?.preventDefault();
        handleDeleteFile<IIncitantFormData>(name, setData);
    };

    const handleSubmit = () => {
        void handleSubmitAsync();
    };

    const handleSubmitAsync = async () => {
        const validationErrors = validateIncitantForm(data, translations);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            try {
                if (typeof window.getCaptchaToken !== 'function') {
                    throw new Error('Captcha is not available');
                }

                const captchaToken = await window.getCaptchaToken(
                    'prime_incitant_request'
                );

                if (!captchaToken) {
                    throw new Error('Captcha token is empty');
                }

                const response = await PrimeIncitantService.pushData(
                    data,
                    captchaToken
                );

                if (
                    response &&
                    response.status === 200 &&
                    (await response.json()) === true
                ) {
                    setIsSubmit(true);
                    setIsSuccess(true);
                } else {
                    setIsSubmit(true);
                    setIsSuccess(false);
                }
            } catch (error) {
                console.error('Erreur lors de la soumission:', error);
                setIsSubmit(true);
                setIsSuccess(false);
            }
        }
    };

    if (isSubmit) {
        return (
            <PrimeSuccessWidget
                isSuccess={isSuccess}
                dictSuccessText1={translations['Site.Prime.SuccessTextFpie1']}
                dictSuccessText2=""
                dictSuccessContact1={
                    translations['Site.Prime.SuccessContactEmployeur']
                }
                dictSuccessContact2=""
                dictFailLabel={''}
                dictSuccessPhone={''}
                dictSuccessSignature1={''}
                dictSuccessSignature2={''}
            />
        );
    }

    return (
        <form
            id="primeForm"
            data-skip-captcha="true"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <IdentificationPartialForm
                values={data}
                onChangeNumberCompany={handleChangeNumberCompany}
                onChange={handleChange}
                errors={errors}
                dictNameLabel={translations['Site.Prime.Name']}
                dictSurnameLabel={translations['Site.Prime.Surname']}
                dictDenominationCompanyLabel={
                    translations['Site.Prime.DenominationCompany']
                }
                dictNumberCompanyLabel={
                    translations['Site.Prime.NumberCompany']
                }
                dictSocialCompanyLabel={
                    translations['Site.Prime.SocialCompany']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
            />

            <AddressPartialForm
                values={data}
                onChange={handleChange}
                onChangeNumberAddress={handleChangeNumberAddress}
                errors={errors}
                dictAddressStreetLabel={
                    translations['Site.Prime.AddressStreet']
                }
                dictAddressNumberLabel={
                    translations['Site.Prime.AddressNumber']
                }
                dictAddressPostalLabel={
                    translations['Site.Prime.AddressPostal']
                }
                dictAddressLocalityLabel={
                    translations['Site.Prime.AddressLocality']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
            />

            <ContactPartialForm
                values={data}
                onChange={handleChange}
                onChangePhone={handleChangePhone}
                onIbanCheck={handleBlurNumberCompanyVoid}
                errors={errors}
                dictMailLabel={translations['Site.Prime.MailCompany']}
                dictMailConfirmationLabel={
                    translations['Site.Prime.MailCompanyConfirmation']
                }
                dictPhoneLabel={translations['Site.Prime.PhoneCompany']}
                dictPhoneConfirmationLabel={
                    translations['Site.Prime.PhoneConfirmation']
                }
                dictIbanCompanyLabel={translations['Site.Prime.IbanCompany']}
                dictBicCompanyLabel={translations['Site.Prime.BicCompany']}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictMailConfirmationError={
                    translations['Site.Prime.MailCompanyConfirmationError']
                }
                dictPhoneConfirmationError={
                    translations['Site.Prime.PhoneConfirmationError']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                isIbanOk={data.iban.length >= 16}
                isBelgianIban={data.iban.startsWith('BE')}
            />

            <SectorPartialForm
                language={language}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictCommissionCompanyLabel={
                    translations['Site.Prime.CommissionCompany']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                values={data}
                errors={errors}
                onSelectSector={handleSelectSector}
                onChangeCommission={handleChangeCommission}
                onKeyDownNumber={(e) => {
                    if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== 'Backspace' &&
                        e.key !== 'Tab'
                    ) {
                        e.preventDefault();
                    }
                }}
            />

            <FloatLabel
                active={!!data.trainingName}
                label={translations['Site.Prime.TrainingNameLabel']}
                labelId={'trainingName'}
            >
                <input
                    type="text"
                    name="trainingName"
                    value={data.trainingName}
                    onChange={(e) =>
                        handleChange('trainingName', e.target.value)
                    }
                />
            </FloatLabel>
            {errors.trainingName && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}

            <FloatLabel
                active={!!data.trainingRegistrationDate}
                label={translations['Site.Prime.TrainingRegistrationDateLabel']}
                labelId={'trainingRegistrationDate'}
            >
                <input
                    type="date"
                    name="trainingRegistrationDate"
                    value={data.trainingRegistrationDate}
                    onChange={(e) =>
                        handleChange('trainingRegistrationDate', e.target.value)
                    }
                />
            </FloatLabel>
            {errors.trainingRegistrationDate && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}

            <FloatLabel
                active={!!data.workerNiss}
                label={translations['Site.Prime.WorkerNissLabel']}
                labelId={'workerNiss'}
            >
                <input
                    type="text"
                    name="workerNiss"
                    value={data.workerNiss}
                    onChange={handleChangeNationalRegisterNumber}
                />
            </FloatLabel>
            {errors.workerNiss && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}
            {errors.workerNiss && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.IdentificationCodeError']}
                    </label>
                </div>
            )}

            <FloatLabel
                active={!!data.workerName}
                label={translations['Site.Prime.WorkerNameLabel']}
                labelId={'workerName'}
            >
                <input
                    type="text"
                    name="workerName"
                    value={data.workerName}
                    onChange={(e) => handleChange('workerName', e.target.value)}
                />
            </FloatLabel>
            {errors.workerName && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}

            <FloatLabel
                active={!!data.workerSurname}
                label={translations['Site.Prime.WorkerSurnameLabel']}
                labelId={'workerSurname'}
            >
                <input
                    type="text"
                    name="workerSurname"
                    value={data.workerSurname}
                    onChange={(e) =>
                        handleChange('workerSurname', e.target.value)
                    }
                />
            </FloatLabel>
            {errors.workerSurname && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}

            <div className="col-12">
                <h3 className="h3">{translations['Site.Prime.TitleFiles']}</h3>
                <div className="row px-4">
                    <div className={'col-12 pb-4 border'} style={borderRadius}>
                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.BankAttestationFile'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.BankAttestationFileInfo'
                                    ]
                                }
                                file={data.bankAttestationFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.bankAttestationFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="bankAttestationFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>

                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.EmploymentContractFile'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.EmploymentContractFileInfo'
                                    ]
                                }
                                file={data.employmentContractFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.employmentContractFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="employmentContractFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>

                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.TrainingInvoiceFileLabel'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.TrainingInvoiceFileInfoLabel'
                                    ]
                                }
                                file={data.trainingInvoiceFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.trainingInvoiceFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="trainingInvoiceFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>

                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.TrainingPaymentFileLabel'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.TrainingPaymentFileInfoLabel'
                                    ]
                                }
                                file={data.trainingPaymentFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.trainingPaymentFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="trainingPaymentFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 text-left pt-3" style={borderRadius}>
                <input
                    type="checkbox"
                    id="dataAreCorrect"
                    name="dataAreCorrect"
                    onChange={(e) =>
                        handleChange('dataAreCorrect', e.target.checked)
                    }
                />
                <label htmlFor="dataAreCorrect">
                    {translations['Site.Prime.DataAreCorrectLabel']}
                </label>
                {errors.dataAreCorrect && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {translations['Site.Prime.ErrorSubmit']}
                        </label>
                    </div>
                )}
            </div>

            <div className="col-12 text-right pt-3">
                <label className="alert-light">
                    {translations['Site.PrimePhoenix.MandatoryField']}
                </label>
            </div>

            <div className="form-group submit mt-5">
                <button type="submit" className="btn-block">
                    <i className="icon-check"></i>
                    {translations['Site.FondsRebond.Submit']}
                </button>
            </div>
        </form>
    );
};

WidgetHelper.register(PrimeIncitantForm, 'prime-incitant-form-widget');
